import type { TFunction } from 'i18next';
import type { Milestone, MilestoneDeliveryStatus } from '../types';

/** Shared by the summary, graph cards, mobile layout and detail dialog. */
export function effectiveTimelineDeliveryStatus(milestone: Milestone): MilestoneDeliveryStatus | undefined {
  const status = milestone.deliveryStatus;
  if (['paid', 'payout_processing', 'payout_failed', 'disputed', 'conflict', 'external_dispute', 'under_resolution'].includes(status ?? '')) return status;
  if (milestone.companyAttestation === 'approved' && milestone.personAttestation === 'approved') return 'jointly_attested';
  if (milestone.companyAttestation === 'approved') return 'seller_attested';
  if (milestone.personAttestation === 'approved') return 'in_progress';
  return status === 'jointly_attested' ? 'in_progress' : status;
}

export function timelineDeliveryPresentation(status: MilestoneDeliveryStatus, t: TFunction, subject = '') {
  const presentation = (() => {
    switch (status) {
      case 'not_started':
        return {
          status: status,
          alertClass: 'alert-secondary',
          badgeClass: 'bg-secondary',
          icon: 'clock',
          label: t('contractTimeline.notStarted', 'Ej påbörjad'),
          event: t('contractTimeline.notStartedEvent', '{{subject}} har inte påbörjats.', { subject })
        };
      case 'in_progress':
        return {
          status: status,
          alertClass: 'alert-primary',
          badgeClass: 'bg-primary',
          icon: 'play-circle',
          label: t('contractTimeline.inProgress', 'Pågår'),
          event: t('contractTimeline.inProgressEvent', 'Arbete pågår för {{subject}}.', { subject })
        };
      case 'ready_for_attestation':
        return {
          status: status,
          alertClass: 'alert-info',
          badgeClass: 'bg-info text-dark',
          icon: 'flag',
          label: t('contractTimeline.readyForAttestation', 'Klar för attestering'),
          event: t('contractTimeline.readyForAttestationEvent', '{{subject}} väntar på hantverkarens attestering.', { subject })
        };
      case 'ready_for_reattestation':
        return {
          status: status,
          alertClass: 'alert-info',
          badgeClass: 'bg-info text-dark',
          icon: 'refresh-cw',
          label: t('contractTimeline.readyForReattestation', 'Klar för ny attestering'),
          event: t('contractTimeline.readyForReattestationEvent', '{{subject}} är åtgärdad och väntar på hantverkarens nya attestering.', { subject })
        };
      case 'seller_attested':
        return {
          status: status,
          alertClass: 'alert-primary',
          badgeClass: 'bg-primary',
          icon: 'eye',
          label: t('contractTimeline.underReview', 'Granskas'),
          event: t('contractTimeline.underReviewEvent', '{{subject}} väntar på beställarens attestering.', { subject })
        };
      case 'jointly_attested':
        return {
          status: status,
          alertClass: 'alert-success',
          badgeClass: 'bg-success',
          icon: 'check-circle',
          label: t('contractTimeline.approved', 'Godkänd'),
          event: t('contractTimeline.approvedEvent', '{{subject}} är godkänd och väntar på utbetalning.', { subject })
        };
      case 'payout_processing':
        return {
          status: status,
          alertClass: 'alert-warning',
          badgeClass: 'bg-warning text-dark',
          icon: 'clock',
          label: t('contractTimeline.payoutInProgress', 'Utbetalning pågår'),
          event: t('contractTimeline.payoutInProgressEvent', 'Utbetalningen för {{subject}} behandlas.', { subject })
        };
      case 'payout_failed':
        return {
          status: status,
          alertClass: 'alert-danger',
          badgeClass: 'bg-danger',
          icon: 'alert-octagon',
          label: t('contractTimeline.payoutFailed', 'Utbetalningsfel'),
          event: t('contractTimeline.payoutFailedEvent', 'Utbetalningen för {{subject}} behöver åtgärdas.', { subject })
        };
      case 'paid':
        return {
          status: status,
          alertClass: 'alert-success',
          badgeClass: 'bg-success',
          icon: 'check-circle',
          label: t('contractTimeline.paid', 'Utbetald'),
          event: t('contractTimeline.paidEvent', '{{subject}} är attesterad och utbetald.', { subject })
        };
      case 'under_resolution':
        return {
          status: status,
          alertClass: 'alert-warning',
          badgeClass: 'bg-warning text-dark',
          icon: 'tool',
          label: t('contractTimeline.beingRemedied', 'Åtgärdas'),
          event: t(
            'contractTimeline.remediationEvent',
            '{{subject}} åtgärdas av hantverkaren.',
            { subject }
          )
        };
      case 'disputed':
        return {
          status: status,
          alertClass: 'alert-warning',
          badgeClass: 'bg-warning text-dark',
          icon: 'alert-triangle',
          label: t('contractTimeline.disputed', 'Under invändning'),
          event: t(
            'contractTimeline.disputedEvent',
            'Beställaren har lämnat en invändning mot {{subject}}.',
            { subject }
          )
        };
      case 'conflict':
        return {
          status: status,
          alertClass: 'alert-danger',
          badgeClass: 'bg-danger',
          icon: 'alert-triangle',
          label: t('contractTimeline.conflict', 'Konflikt'),
          event: t(
            'contractTimeline.conflictEvent',
            '{{subject}} är i konfliktläge.',
            { subject }
          )
        };
      case 'external_dispute':
        return {
          status: status,
          alertClass: 'alert-danger',
          badgeClass: 'bg-danger',
          icon: 'shield',
          label: t('contractTimeline.dispute', 'Tvist'),
          event: t(
            'contractTimeline.externalDisputeEvent',
            '{{subject}} hanteras som extern tvist.',
            { subject }
          )
        };
      default:
        return null;
    }
  })();
  // Unknown server values stay neutral.
  const result = presentation ?? {
    status, alertClass: 'alert-secondary', badgeClass: 'bg-secondary', icon: 'clock',
    label: t('milestoneStarts.statusNotStarted'), event: ''
  };
  const color = result.alertClass === 'alert-danger' ? '#dc3545'
    : result.alertClass === 'alert-warning' ? '#f0ad4e'
      : result.alertClass === 'alert-success' ? '#2e9d59'
        : result.alertClass === 'alert-secondary' ? '#adb5bd' : '#3454d1';
  return {
    ...result, color,
    cardLabel: status === 'in_progress' ? t('milestoneStarts.statusInProgress')
      : status === 'not_started' ? t('milestoneStarts.statusNotStarted') : result.label,
    cardIcon: result.icon === 'check-circle' ? 'check' : result.icon === 'play-circle' ? 'play' : result.icon
  };
}
